import { QueueCard, Milestones, CloseBrowserNote, LearnSlides } from "@/components/CreationExperience";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  Loader2, CheckCircle2, AlertTriangle, Compass, PenLine, Palette,
  Sparkles, ChevronDown, Terminal, BookOpen, FileText,
} from "lucide-react";

const MODEL_META = {
  gpt: { name: "GPT", color: "#10A37F", testid: "ai-model-gpt-badge" },
  claude: { name: "Claude", color: "#D97706", testid: "ai-model-claude-badge" },
  gemini: { name: "Gemini", color: "#3B82F6", testid: "ai-model-gemini-badge" },
  system: { name: "KLYR", color: "#E5C158", testid: "ai-model-system-badge" },
};

const AGENTS = [
  { key: "gpt", name: "GPT", icon: Compass },
  { key: "claude", name: "Claude", icon: PenLine },
  { key: "gemini", name: "Gemini", icon: Palette },
];
const ACTIVITY_MODES = ["sudoku", "maze", "coloring", "wordsearch", "journal", "workbook"];
const PUZZLE_MODES = ["sudoku", "maze", "coloring", "wordsearch"];

const MODE_LABEL = {
  nonfiction: "Non-fiction", fiction: "Fiction", children: "Children's", sudoku: "Sudoku",
  maze: "Maze", coloring: "Coloring", wordsearch: "Word Search", journal: "Journal", workbook: "Workbook",
};
const PAGE_MODES = ["sudoku", "maze", "coloring", "wordsearch", "workbook", "journal", "children"];

function descriptors(book) {
  const out = [];
  if (book.mode === "fiction") { if (book.tone) out.push(book.tone); out.push("Full length"); }
  else if (book.mode === "nonfiction") {
    out.push("Non-fiction"); out.push(book.nf_format === "narrative" ? "Narrative" : "Reference guide");
    if (book.tone) out.push(book.tone);
  } else { out.push(MODE_LABEL[book.mode] || "Book"); if (book.tone) out.push(book.tone); }
  const total = book.num_chapters || (book.chapters?.length || 0);
  if (total) out.push(`${total} ${PAGE_MODES.includes(book.mode) ? "pages" : "chapters"}`);
  return out.filter(Boolean);
}

// Strip markdown and note-style labels so user-facing text never shows raw ** / ## / "Characters:".
function plainLines(md) {
  return (md || "").split("\n")
    .map((l) => l.replace(/[*_#`>]+/g, "").replace(/^\s*[-•]\s*/, "").trim())
    .filter((l) => l && !/^[A-Z][A-Za-z '&/]{0,30}:?$/.test(l))
    .map((l) => l.replace(/^[A-Z][A-Za-z ']{0,24}:\s+/, "").replace(/\s+/g, " "));
}
export const plainText = (md) => plainLines(md).join(" ");

// A concise 1–2 full-sentence preview: the clean logline first, else the book's own synopsis (never the raw prompt).
function summaryPreview(book) {
  if ((book.logline || "").trim()) return plainText(book.logline);
  const sentences = plainLines(book.story_synopsis).flatMap((l) => l.match(/[^.!?]+[.!?]+/g) || []).map((x) => x.trim())
    .filter((x) => x.split(" ").length >= 6 && !x.includes("/"));
  if (!sentences.length) return "";
  let out = sentences.slice(0, 2).join(" ");
  if (out.length > 190) out = (sentences[0].length <= 190 ? sentences[0] : sentences[0].slice(0, 187).replace(/\s+\S*$/, "") + "…");
  return out;
}

const minutesSince = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 60000 : 0);

// Events the user must know about: resumed after a restart, Character Bible differences, restored drafts.
const BookNotices = ({ book }) => {
  const notes = book.notices || [];
  if (!notes.length) return null;
  return (
    <div data-testid="book-notices" className="max-w-2xl mx-auto space-y-2 text-left">
      {notes.map((n, i) => (
        <div key={i} data-testid={`book-notice-${n.type}`}
          className={`flex gap-2.5 rounded-xl border px-4 py-3 font-ui text-sm ${n.type === "contract_deviation"
            ? "border-amber-400/30 bg-amber-400/[0.06] text-amber-100" : "border-gold-500/20 bg-white/[0.03] text-slate-200"}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-gold-300" />
          <span>{n.message}</span>
        </div>
      ))}
    </div>
  );
};

// Live count chips (chapters · pages · words) that populate progressively.
function metaChips(book) {
  const isPaged = PAGE_MODES.includes(book.mode);
  const total = book.num_chapters || (book.chapters?.length || 0);
  const chips = [];
  if (isPaged) {
    if (book.target_pages || total) chips.push(`${book.target_pages || total} pages`);
  } else {
    if (total) chips.push(`${total} chapters`);
    if (book.target_pages) chips.push(`${book.target_pages} pages`);
  }
  if (book.word_count) chips.push(`${book.word_count.toLocaleString()} words`);
  return chips;
}

// Structured creation parameters surfaced inside the expanded Creative Brief.
function briefParams(book) {
  const rows = [];
  let type = MODE_LABEL[book.mode] || "Book";
  if (book.mode === "nonfiction") type += book.nf_format === "narrative" ? " · Narrative" : " · Reference";
  rows.push(["Type", type]);
  if (book.tone) rows.push(["Tone", book.tone]);
  const len = PAGE_MODES.includes(book.mode)
    ? `${book.target_pages || book.num_chapters} pages`
    : `${book.num_chapters} chapters · ${book.target_pages} pages`;
  rows.push(["Target length", len]);
  if (book.audience && book.audience !== "Auto") rows.push(["Audience", book.audience]);
  if (book.pov && book.pov !== "Auto") rows.push(["Point of view", book.pov]);
  if (book.outline_structure && book.outline_structure !== "Auto") rows.push(["Structure", book.outline_structure]);
  rows.push(["Illustrations", book.include_images ? "On" : "Off"]);
  return rows;
}

// Models this pipeline is expected to use (activity books never call Claude).
function expectedAgents(book) {
  if (!ACTIVITY_MODES.includes(book.mode)) return ["gpt", "claude", "gemini"];
  return book.include_images || PUZZLE_MODES.includes(book.mode) ? ["gpt", "gemini"] : ["gpt"];
}

const cleanTask = (msg = "") =>
  msg.replace(/^(GPT|Claude|Gemini) is /i, "").replace(/[✓…]+\s*$/, "").replace(/\.{3}$/, "").trim()
    .replace(/^./, (c) => c.toUpperCase());

// Clean structured summary for the completed screen (never the raw request).
function readyMeta(book, count) {
  if (PUZZLE_MODES.includes(book.mode)) {
    const unit = book.mode === "coloring" ? "Coloring Pages" : `${MODE_LABEL[book.mode]} Puzzles`;
    return [`${count} ${unit}`, book.mode !== "coloring" && book.difficulty
      ? book.difficulty[0].toUpperCase() + book.difficulty.slice(1) : null].filter(Boolean).join("  ·  ");
  }
  return [count ? `${count} ${PAGE_MODES.includes(book.mode) ? "pages" : "chapters"}` : null,
    book.word_count ? `${book.word_count.toLocaleString()} words` : null].filter(Boolean).join("  ·  ");
}

// Agent state derived from the activity log (authoritative): start/done events per model.
export function deriveAgents(book) {
  const finished = book.status === "done" || book.status === "error";
  const expected = expectedAgents(book);
  const seen = {};
  for (const l of book.logs || []) {
    if (!["gpt", "claude", "gemini"].includes(l.model)) continue;
    const ev = l.event || ((l.message || "").includes("✓") ? "done" : "start");
    const s = (seen[l.model] = seen[l.model] || { open: false, task: "", current: "" });
    if (ev === "start") {
      if (!s.open || l.event === "start") s.task = cleanTask(l.message);
      s.open = true; s.current = cleanTask(l.message);
    } else { s.open = false; if (!s.task) s.task = cleanTask(l.message); }
  }
  return (key) => {
    const s = seen[key];
    if (!s) return { state: finished || !expected.includes(key) ? "unused" : "waiting", task: "" };
    const active = s.open && !finished;
    return { state: active ? "thinking" : "complete", task: active ? s.current : s.task };
  };
}

function stageLine(book) {
  if (book.status === "done") return "Finished";
  if (["writing", "generating"].includes(book.status) && Array.isArray(book.chapters) && book.chapters.length) {
    const total = book.num_chapters || book.chapters.length;
    const cur = book.chapters.find((c) => !(c.content && c.content.trim()));
    const written = book.chapters.filter((c) => c.content && c.content.trim()).length;
    if (cur && cur.title) {
      const unit = PAGE_MODES.includes(book.mode) ? "Page" : "Chapter";
      return `Writing ${unit} ${Math.min(written + 1, total)} of ${total} · ${cur.title}`;
    }
  }
  return book.current_step || "Preparing your studio";
}

const STATE_LABEL = { thinking: "Active", complete: "Complete", waiting: "Waiting", unused: "Not used" };

const AgentNode = ({ agent, state, task, reduce }) => {
  const Icon = agent.icon;
  const detail = state === "unused" ? "Not used for this book type" : state === "waiting" ? "Waiting to start" : task;
  const active = state === "thinking";
  const complete = state === "complete";
  return (
    <motion.div
      data-testid={`agent-node-${agent.key}`}
      data-state={state}
      initial={false}
      animate={{ opacity: state === "waiting" || state === "unused" ? 0.5 : 1, scale: complete ? 0.97 : 1 }}
      whileHover={reduce ? {} : { y: -3 }}
      transition={{ duration: 0.5 }}
      className={`relative rounded-2xl p-4 sm:p-5 backdrop-blur-xl border transition-colors
        ${active ? "border-gold-400/50 bg-gold-400/[0.06]" : complete ? "border-emerald-400/25 bg-white/[0.02]" : "border-white/8 bg-white/[0.02]"}`}
      style={active && !reduce ? { boxShadow: "0 0 34px rgba(229,193,88,0.18)" } : {}}
    >
      <div className="flex items-center gap-3">
        <div className={`shrink-0 w-10 h-10 rounded-xl grid place-items-center border
          ${active ? "border-gold-400/50 bg-gold-400/10" : complete ? "border-emerald-400/30 bg-emerald-400/5" : "border-white/10 bg-white/[0.03]"}`}>
          {complete ? <CheckCircle2 className="w-5 h-5 text-emerald-300" />
            : active ? <Icon className="w-5 h-5 text-gold-300" />
              : <Icon className="w-5 h-5 text-slate-500" />}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display text-base text-slate-100">{agent.name}</span>
            {active && !reduce && <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-pulse" />}
          </div>
          <div data-testid={`agent-state-${agent.key}`} className={`font-ui text-[11px] uppercase tracking-[0.18em] mt-0.5
            ${active ? "text-gold-300" : complete ? "text-emerald-300/80" : "text-slate-500"}`}>
            {STATE_LABEL[state]}
          </div>
        </div>
      </div>
      {detail && (
        <p data-testid={`agent-task-${agent.key}`}
          className={`font-read text-sm leading-relaxed mt-3 ${active ? "text-slate-300" : "text-slate-500"}`}>
          {detail}
        </p>
      )}
    </motion.div>
  );
};

// Smooth crossfade for a single field as it progressively populates.
const Crossfade = ({ k, className, children }) => (
  <AnimatePresence mode="wait" initial={false}>
    <motion.div key={k} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.4 }} className={className}>
      {children}
    </motion.div>
  </AnimatePresence>
);

// A live "library card" of the book being created — title, counts & a concise synopsis preview.
const BookCard = ({ book, reduce }) => {
  const title = (book.title || "").trim();
  const running = book.status !== "error";
  const titleText = title || (running ? "Crafting your title…" : "Untitled manuscript");
  const chips = metaChips(book);
  const preview = summaryPreview(book);
  const summaryText = preview
    || (book.queue?.state === "waiting" ? "Waiting for a writing slot…"
      : (book.progress || 0) < 30 ? "Planning your book…" : "Preparing summary…");
  const titlePlaceholder = !title;
  const summaryPlaceholder = !preview;
  const pulse = reduce ? "" : "animate-pulse";

  return (
    <motion.div data-testid="book-card"
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
      className="max-w-2xl mx-auto rounded-2xl border border-gold-500/15 bg-white/[0.035] backdrop-blur-xl p-5 sm:p-6">
      <div className="flex gap-4 sm:gap-5">
        <div className={`hidden sm:grid shrink-0 w-14 h-20 rounded-md border border-gold-400/30 place-items-center
          bg-gradient-to-b from-navy-800 to-navy-950 ${reduce ? "" : "shadow-[0_0_26px_rgba(229,193,88,0.14)]"}`}>
          <BookOpen className="w-5 h-5 text-gold-300/80" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-ui text-[10px] uppercase tracking-[0.28em] text-gold-400/80 mb-1.5">Now creating</div>
          <Crossfade k={titleText} className={`font-display text-xl sm:text-2xl leading-snug
            ${titlePlaceholder ? `text-slate-300/70 italic ${pulse}` : "text-slate-50"}`}>
            {titleText}
          </Crossfade>
          {chips.length > 0 && (
            <Crossfade k={chips.join("|")} className="mt-1.5 font-ui text-sm text-gold-200/90">
              <span data-testid="book-card-meta">{chips.join("  ·  ")}</span>
            </Crossfade>
          )}
          <Crossfade k={summaryText} className={`mt-2.5 font-read text-sm leading-relaxed
            ${summaryPlaceholder ? `text-slate-400/80 italic ${pulse}` : "text-slate-300/90"}`}>
            <span data-testid="book-card-summary">{summaryText}</span>
          </Crossfade>
        </div>
      </div>
    </motion.div>
  );
};

export const ProgressPanel = ({ book, onOpen }) => {
  const logRef = useRef(null);
  const reduce = useReducedMotion();
  const [showBrief, setShowBrief] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [book?.logs?.length, showLogs]);

  if (!book) return null;
  const isError = book.status === "error";
  const done = book.status === "done";
  const agentOf = deriveAgents(book);
  const stateOf = (key) => agentOf(key).state;
  const brief = (book.topic || "").trim();
  const params = briefParams(book);
  const chapterCount = (book.chapters || []).filter((c) => !c.no_number).length || book.num_chapters || 0;
  const klyrState = done ? "complete" : (book.progress || 0) >= 97 ? "thinking" : (book.progress || 0) >= 30 ? "waiting" : "waiting";
  const klyrActive = klyrState === "thinking" || done;

  return (
    <div data-testid="ai-research-container" className="relative">
      {/* Ambient depth layers */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-[32px]">
        <div className={`absolute inset-0 ${reduce ? "" : "klyr-ambient"}`} />
        {!reduce && (
          <>
            <div className="klyr-page-frag hidden sm:block" style={{ width: 120, height: 160, left: "6%", top: "18%", "--r": "-8deg", animationDelay: "0s" }} />
            <div className="klyr-page-frag hidden sm:block" style={{ width: 90, height: 120, right: "8%", top: "30%", "--r": "6deg", animationDelay: "3s" }} />
            <div className="klyr-page-frag hidden md:block" style={{ width: 70, height: 96, right: "16%", bottom: "12%", "--r": "-4deg", animationDelay: "6s" }} />
          </>
        )}
      </div>

      <div className="max-w-4xl mx-auto px-1 py-8 sm:py-10">
        <AnimatePresence mode="wait">
          {!done ? (
            <motion.div key="working" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.5 }} className="space-y-7">
              {/* Header */}
              <div data-testid="compiling-header" className="text-center space-y-2">
                <div className="font-ui text-xs uppercase tracking-[0.35em] text-gold-400">Now Compiling</div>
                <h2 className="font-display text-3xl sm:text-4xl font-medium text-slate-50">Your book is taking shape.</h2>
                <div className="font-ui text-sm text-slate-400">{descriptors(book).join("  ·  ")}</div>
              </div>

              {/* Live book card */}
              <BookCard book={book} reduce={reduce} />
              <BookNotices book={book} />

              {/* Creative brief — collapsed by default */}
              {(brief || params.length > 0) && (
                <div className="max-w-2xl mx-auto -mt-2">
                  <button data-testid="creative-brief-toggle" onClick={() => setShowBrief((s) => !s)}
                    className="w-full flex items-center justify-between font-ui text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-gold-300 transition-colors px-1 py-2">
                    <span className="flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> View Creative Brief</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showBrief ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {showBrief && (
                      <motion.div data-testid="creative-brief-panel"
                        initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="rounded-2xl border border-gold-500/12 bg-white/[0.03] backdrop-blur-xl p-5 space-y-4">
                          {brief && (
                            <div>
                              <div className="font-ui text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Your request</div>
                              <p className="font-read text-sm text-slate-300 leading-relaxed whitespace-pre-line">{brief}</p>
                            </div>
                          )}
                          {params.length > 0 && (
                            <div>
                              <div className="font-ui text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Creation settings</div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                                {params.map(([k, v]) => (
                                  <div key={k} className="flex items-baseline justify-between gap-3 border-b border-white/5 pb-1.5">
                                    <span className="font-ui text-xs text-slate-500">{k}</span>
                                    <span className="font-ui text-sm text-slate-200 text-right">{v}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Creation Experience: real queue position + real stages, no percentages or time estimates */}
              <div className="max-w-2xl mx-auto space-y-4" data-testid="overall-progress">
                <QueueCard book={book} />
                <Milestones book={book} />
                <div data-testid="current-stage" className="flex items-center gap-2 font-ui text-sm text-slate-300">
                  {isError ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <Loader2 className="w-4 h-4 text-gold-400 animate-spin" />}
                  {stageLine(book)}
                </div>
                {!isError && minutesSince(book.run_heartbeat || book.logs?.[book.logs.length - 1]?.time) > 4 && (
                  <p data-testid="progress-stale-note" className="font-ui text-xs text-amber-200/90">
                    No update for {Math.round(minutesSince(book.run_heartbeat || book.logs?.[book.logs.length - 1]?.time))} minutes.
                    Your book keeps generating on our servers even if you leave; if the server restarted, it resumes automatically from the last finished chapter.
                  </p>
                )}
                <CloseBrowserNote />
              </div>
              <LearnSlides />

              {/* KLYR Intelligence workspace */}
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {AGENTS.map((a) => {
                    const s = agentOf(a.key);
                    return <AgentNode key={a.key} agent={a} state={s.state} task={s.task} reduce={reduce} />;
                  })}
                </div>

                {/* Converging threads (desktop) */}
                <svg className="hidden sm:block w-full h-14" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden>
                  <defs>
                    <linearGradient id="klyrThread" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#E5C158" stopOpacity="0.1" />
                      <stop offset="100%" stopColor="#E5C158" stopOpacity="0.55" />
                    </linearGradient>
                  </defs>
                  {[16.7, 50, 83.3].map((x, i) => {
                    const key = ["gpt", "claude", "gemini"][i];
                    const lit = ["thinking", "complete"].includes(stateOf(key));
                    return (
                      <path key={i} d={`M ${x} 0 C ${x} 20, 50 20, 50 40`} fill="none"
                        stroke={lit ? "url(#klyrThread)" : "rgba(255,255,255,0.08)"} strokeWidth="0.8"
                        className={lit && !reduce ? "klyr-thread" : ""} strokeLinecap="round" />
                    );
                  })}
                </svg>
                <div className="sm:hidden h-6 flex justify-center"><div className="w-px h-full bg-gradient-to-b from-gold-500/10 to-gold-400/40" /></div>

                {/* KLYR synthesis core */}
                <motion.div data-testid="agent-node-klyr" data-state={klyrState}
                  animate={{ opacity: klyrActive ? 1 : 0.6 }}
                  className={`mx-auto max-w-md rounded-2xl border backdrop-blur-xl px-5 py-5 text-center
                    ${klyrActive ? "border-gold-400/50 bg-gold-400/[0.07]" : "border-white/8 bg-white/[0.02]"} ${klyrActive && !reduce ? "klyr-core-glow" : ""}`}>
                  <div className="inline-flex items-center gap-2">
                    <Sparkles className={`w-5 h-5 ${klyrActive ? "text-gold-300" : "text-slate-500"}`} />
                    <span className="font-display text-xl tracking-wide text-slate-50">KLYR Synthesis</span>
                  </div>
                  <p className={`font-ui text-[11px] uppercase tracking-[0.18em] mt-1 ${klyrActive ? "text-gold-300" : "text-slate-500"}`}>
                    {done ? "Complete" : klyrActive ? "Weaving the final manuscript" : "Awaiting the agents"}
                  </p>
                </motion.div>
              </div>

              {/* Live activity (collapsible) */}
              <div className="max-w-2xl mx-auto">
                <button data-testid="live-activity-toggle" onClick={() => setShowLogs((s) => !s)}
                  className="w-full flex items-center justify-between font-ui text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-gold-300 transition-colors px-1 py-2">
                  <span className="flex items-center gap-2"><Terminal className="w-3.5 h-3.5" /> Live activity</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showLogs ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {showLogs && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div ref={logRef} data-testid="research-log"
                        className="bg-navy-950/70 border border-gold-500/12 rounded-2xl p-4 h-56 overflow-y-auto scroll-thin font-mono2 text-[12px] space-y-1.5">
                        {(book.logs || []).map((log, i) => {
                          const m = MODEL_META[log.model] || MODEL_META.system;
                          return (
                            <div key={i} className="flex gap-3 items-start">
                              <span className="shrink-0 font-semibold uppercase text-[10px] mt-0.5 tracking-wider" style={{ color: m.color }}>{m.name}</span>
                              <span className="text-slate-400 leading-relaxed">{log.message}</span>
                            </div>
                          );
                        })}
                        {!done && !isError && <div className="flex gap-2 items-center text-slate-500"><span className="w-1.5 h-1.5 bg-gold-400 rounded-full animate-pulse" /> working…</div>}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {isError && (
                <div className="max-w-2xl mx-auto text-center font-ui text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  Something went wrong: {book.error || "unknown error"}. Please try again.
                </div>
              )}
            </motion.div>
          ) : (
            /* ---- Completed state (same screen transforms) ---- */
            <motion.div key="ready" data-testid="book-ready-panel"
              initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
              className="text-center py-10 space-y-6">
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1, type: "spring", stiffness: 160, damping: 16 }}
                className={`mx-auto w-16 h-16 rounded-2xl grid place-items-center border border-gold-400/40 bg-gold-400/10 ${reduce ? "" : "klyr-core-glow"}`}>
                <Sparkles className="w-8 h-8 text-gold-300" />
              </motion.div>
              <div className="space-y-2">
                <div className="font-ui text-xs uppercase tracking-[0.35em] text-gold-400">Complete</div>
                <h2 className="font-display text-4xl sm:text-5xl font-medium text-slate-50">Your book is ready.</h2>
                <p className="font-display text-xl gold-text">{book.title}</p>
                <p data-testid="book-ready-meta" className="font-ui text-sm text-slate-400">
                  {readyMeta(book, chapterCount)}
                </p>
                {book.print_pages ? (
                  <p data-testid="book-ready-print-pages" className="font-ui text-xs text-slate-500 mt-1">
                    {book.print_pages} print pages
                  </p>
                ) : null}
              </div>
              <BookNotices book={book} />
              <button data-testid="open-book-button" onClick={onOpen}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-gold-300 to-gold-500 text-navy-900 font-ui font-semibold rounded-full px-8 py-3.5 hover:brightness-105 transition-all shadow-[0_0_30px_rgba(229,193,88,0.3)]">
                <BookOpen className="w-5 h-5" /> Open your book
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
